import { Announced, mergeStyleSets, MessageBar, MessageBarType, Stack, Text, useTheme } from '@fluentui/react';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { ApiConfig, CtxApi } from '../../data-types';
import { FilterTypeEnum } from '../../data-types/advanced-filter/FilterTypeEnum';
import { ErrorResponse } from '../../data-types/ErrorResponse';
import { useSearchParams } from '../../hooks/useSearchParams';
import { IDevXGridFilterResponse } from '../../view-models/advanced-filter/IDevXGridFilterResponse';

export interface IAdvancedFilterDevXIndicatorProps {
    getAdvancedFilterDataUrl: string;
}

export const AdvancedFilterDevXIndicator = (props: IAdvancedFilterDevXIndicatorProps) => {
    const { palette } = useTheme();
    const queryStringContext = useSearchParams().queryStringContext;
    const [placeholderData, setPlaceholderData] = React.useState<IDevXGridFilterResponse>();
    const advancedFilterApiConfig = new ApiConfig(props.getAdvancedFilterDataUrl);

    const queryKey = advancedFilterApiConfig.constructQueryKey(undefined, queryStringContext);
    const { data, isError, error } = useQuery<IDevXGridFilterResponse, Error>(
        queryKey,
        async () => await CtxApi.get<IDevXGridFilterResponse>(props.getAdvancedFilterDataUrl, true, queryStringContext),
        {
            placeholderData: placeholderData,
            onSuccess: (data) => {
                setPlaceholderData(data);
            },
        }
    );

    const items = data?.gridFilterItems;
    const gridFilterStrings = items
        ?.map((filterItem) => {
            if (filterItem.selectedValues?.length > 0 && filterItem.selectedValues[0].length > 0) {
                const columnType = data?.gridFilterItems.find((x) => x.columnName === filterItem.columnName)?.columnType;
                let message = `"${filterItem.columnDisplayName}" `;
                message += columnType === FilterTypeEnum.string ? 'contains ' : '= ';
                message += Array.isArray(filterItem.selectedValues)
                    ? `"${filterItem.selectedValues.join(', ')}"`
                    : `"${filterItem.selectedValues}"`;
                return message;
            }
            return undefined;
        })
        .filter((n) => n);
    let announcedFilterMessage = '';
    if (gridFilterStrings && gridFilterStrings.length > 0) {
        announcedFilterMessage = `Advanced Filters Applied: ${gridFilterStrings.join(', ')}`;
    }

    const classNames = mergeStyleSets({
        filterItem: { background: palette.themeLighterAlt, marginRight: 7, padding: 5 },
        filterItemPrefix: {
            padding: 5,
            fontWeight: 'bold',
        },
    });

    if (isError) {
        const errorResponse = new ErrorResponse(error);
        return <MessageBar messageBarType={MessageBarType.error}>{errorResponse.message}</MessageBar>;
    }

    return (
        <Stack horizontal>
            <Announced message={announcedFilterMessage} />
            {gridFilterStrings && gridFilterStrings.length > 0 && (
                <Stack horizontal wrap>
                    <Text className={classNames.filterItemPrefix}>Advanced Filter(s) Applied:</Text>
                    {gridFilterStrings?.map((filter, index) => (
                        <Text key={index} className={classNames.filterItem}>
                            {filter}
                        </Text>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};
